import { makeAutoObservable, observableRef } from 'mobx';
import { token } from '@/infrastructure/container';
import { ServerError } from '@/domain/errors';
import type { ICoachGateway } from '@/domain/coachGateway';
import type { IFrontDeskGateway, RegisterPlayerInput } from '@/domain/gateway';
import { emptySessionState, type SessionEvent, type SessionState } from '@/domain/session';
import type { IAppBus } from './AppBus';
import type { ICeleriantClient, SessionHandle } from './CeleriantClient';
import type { IClock } from './Clock';
import type { IDomConnectivity } from './DomConnectivity';
import type { ILogger } from './Logger';
import { GatewayReach } from './GatewayReach';
import { translate } from './translate';

// The one place the session aggregate meets the app. Above this line nothing imports the SDK and
// nothing sees an SDK error class: `translate()` (src/services/translate.ts) is the boundary
// INVARIANTS rule 17 names, and the reason a store above it can branch on ConflictError without
// knowing what a 409 is.
//
// It is also the only object holding the handle, which is what keeps "one aggregate, one fold"
// true. Every screen reads the same `sessionState` and every write goes through here.

// A teardown callback. Typed here rather than imported as the SDK's `Unsubscribe` so this file
// keeps no SDK edge at all.
type Unsubscribe = () => void;

// The SDK's `execute` correlates kind and payload through a generic. Our events carry the same
// correlation in a discriminated union, and TypeScript cannot see that the two are the same fact.
// This is that seam, and it is the only cast in the file: the union is written against the same
// aggregate definition in src/domain/aggregates.ts that types the handle. Same shape, and for the
// same reason, as `AnyExecute` in ProjectGateway.ts.
type AnyExecute = (
  kind: string,
  payload: unknown,
  opts: { idempotencyKey: string },
) => Promise<unknown>;

// The lifecycle half, kept off IFrontDeskGateway on purpose: the front desk store may write and
// read, it may not decide which session the building is in. That is the shell's call.
export interface ISessionConnection {
  open(sessionId: string): Promise<void>;
  readonly sessionId: string | undefined;
}

export const TOKEN_ISessionConnection = token<ISessionConnection>('ISessionConnection');
export const TOKEN_SessionGateway = token<SessionGateway>('SessionGateway');

export class SessionGateway implements IFrontDeskGateway, ICoachGateway, ISessionConnection {
  private _state: SessionState = emptySessionState();
  private _handle: SessionHandle | undefined;
  // The teardown half of `_handle`. Kept beside it so the two can never drift apart.
  private _unsubscribe: Unsubscribe | undefined;
  private _unsubscribeStatus: Unsubscribe | undefined;
  private _sessionId: string | undefined;
  private _online: boolean;
  private readonly reach: GatewayReach;

  constructor(
    private readonly celeriant: ICeleriantClient,
    bus: IAppBus,
    connectivity: IDomConnectivity,
    clock: IClock,
    private readonly logger: ILogger,
  ) {
    this._online = connectivity.online;
    this.reach = new GatewayReach(clock, logger, () => this.probe());
    // Subscribe in the constructor, not after the async open: the container is lazy and a
    // subscription that waits misses every publish in between (INVARIANTS rule 20).
    bus.subscribe('CONNECTIVITY_CHANGED', (c) => this.applyOnline(c.online));
    // Both refs, not deep proxies. `_state` is a frozen SDK snapshot the domain selectors read;
    // one reference swap per fold is the whole reactivity story. `_handle` is an external SDK
    // object whose getters read non-observable engine state, and deep-proxying it caches those
    // getters as computeds that never invalidate.
    // The two teardown callbacks and the reach probe are plain objects nothing derives from, so
    // they stay out of the graph entirely rather than being handed to MobX as values that later
    // become functions.
    makeAutoObservable<
      SessionGateway,
      '_state' | '_handle' | '_unsubscribe' | '_unsubscribeStatus' | 'reach'
    >(this, {
      _state: observableRef,
      _handle: observableRef,
      _unsubscribe: false,
      _unsubscribeStatus: false,
      reach: false,
    });
  }

  // The current fold, as a MobX observable. Every screen derives from this through the selectors
  // in domain/session.ts.
  get sessionState(): SessionState {
    return this._state;
  }

  // The device's own network interface. The fast local signal: false is reliably offline, true is
  // only a hint (docs/sync.md). A tap rejects on this before the gateway is touched at all.
  get online(): boolean {
    return this._online;
  }

  // Whether the GATEWAY is answering, which `navigator.onLine` cannot tell you: a desk on hall wifi
  // with a dead gateway would otherwise keep its buttons enabled and find out one failed write at a
  // time. See GatewayReach for the clear/latch semantics.
  get gatewayReachable(): boolean {
    return this.reach.reachable;
  }

  // Both halves of "can a write land", ANDed once here because the coach screen has no use for
  // telling them apart: a vote is gone either way and there is nothing a coach mid-drill can do
  // about the gateway. The front desk reads the two separately (IFrontDeskGateway) because a human
  // standing at the desk CAN act on one of them and not the other.
  get connectionOk(): boolean {
    return this.online && this.gatewayReachable;
  }

  get sessionId(): string | undefined {
    return this._sessionId;
  }

  // Opens the handle for one session. Idempotent for the same id; opening a different session
  // swaps the handle, which is what a second session on the same device looks like.
  //
  // No redeemShare here: the share is redeemed by whoever decided which session this is, BEFORE
  // this call (ProjectGateway.openSession, driven by OnboardingStore). Opening an online handle
  // subscribes to realtime immediately and the realtime endpoint refuses an ungranted client
  // (docs/access.md, "Redemption ordering").
  open = async (sessionId: string): Promise<void> => {
    if (this._sessionId === sessionId && this._handle !== undefined) return;
    const handle = await this.celeriant.sessionHandle(sessionId);
    // Tear the old session down FIRST. The SDK's close() sets a flag; it does not drop listeners
    // and it does not cancel a catch-up already in flight, so a subscription left attached can
    // deliver the OLD session's fold after the new one has been attached and write it over the
    // top. Nothing above here would ever see that it happened.
    await this.detach();
    // AWAIT the first catch-up. The SDK's open() fires it FIRE-AND-FORGET
    // (`void this.refresh().catch(console.error)` in onlineEngine.open), so `aggregateOnline`
    // resolves with an empty projection and a version of 0 even for an aggregate full of events.
    // Without this line a device that just joined renders an empty roster and only fills in
    // whenever the next poke happens to arrive (session/scraps.md, the P2 SDK note).
    //
    // A never-created aggregate reads 404 and folds to empty without throwing, so this stays
    // correct for the admin's first write. A refusal or an outage DOES throw, and that is
    // deliberate: a device that cannot read the session must say so rather than show an empty hall.
    await this.firstCatchUp(handle);
    // One reference swap per update. The SDK snapshot is frozen; reassignment is what MobX tracks.
    const unsubscribe = handle.subscribe((state) => this.applyState(state));
    const unsubscribeStatus = handle.subscribeStatus((status) => this.reach.apply(status));
    this.attach(sessionId, handle, unsubscribe, unsubscribeStatus);
    // subscribeStatus does not replay the current value, so seed from the handle. A device that
    // opened against a dead gateway must know before its first tap.
    this.reach.apply(handle.status);
  };

  // ONE write attempt from this app's point of view: one execute(), no loop. The retry ladder
  // lives in the calling store, where the same correlation id and idempotency key are held across
  // attempts. Resolves once the server has acked AND the registration has folded, which OCC
  // guarantees: the write response's version is expected + 1, so the engine folds it before
  // execute() resolves and the desk sees its own player (docs/sync.md).
  registerPlayer = async (input: RegisterPlayerInput): Promise<void> => {
    const handle = this._handle;
    if (handle === undefined) throw new ServerError('front desk: no session is open');
    try {
      await handle.execute(
        'playerRegistered',
        // The field is OMITTED rather than sent as undefined when there is no number: the payload
        // schema declares it optional, and `t.optional` rejects an explicit null or undefined.
        input.number === undefined
          ? { court: input.court, correlationId: input.correlationId }
          : { court: input.court, correlationId: input.correlationId, number: input.number },
        { idempotencyKey: input.idempotencyKey },
      );
    } catch (cause) {
      const failure = translate(cause, 'playerRegistered');
      this.logger.warn(`registerPlayer failed: ${failure.message}`);
      throw failure;
    }
  };

  // The coach's and (in P4) the mover's write path: ONE `occ: false` execute, no ladder. It
  // resolves on the server ack, which is NOT the instant the event reaches `sessionState` — a
  // non-OCC write response carries no version the engine may advance the read cursor with, so the
  // event lands on the next catch-up or realtime poke (docs/sync.md). Nothing here reads state
  // back afterwards, and nothing above may treat this resolution as "it is in the fold now".
  execute = async (event: SessionEvent, opts: { idempotencyKey: string }): Promise<void> => {
    const handle = this._handle;
    if (handle === undefined) throw new ServerError('coach: no session is open');
    const execute = handle.execute as AnyExecute;
    try {
      await execute(event.kind, event.payload, { idempotencyKey: opts.idempotencyKey });
    } catch (cause) {
      const failure = translate(cause, event.kind);
      this.logger.warn(`${event.kind} failed: ${failure.message}`);
      throw failure;
    }
  };

  close = async (): Promise<void> => {
    await this.detach();
  };

  // Actions. makeAutoObservable has wrapped every method, so the SDK subscription and the bus
  // handler above are already batched and this store needs no runInAction (INVARIANTS rule 14).
  private applyState(state: SessionState): void {
    this._state = state;
  }

  private applyOnline(online: boolean): void {
    this._online = online;
  }

  // What GatewayReach runs while the gateway is unreachable. A catch-up read is the cheapest write
  // -free thing that proves the gateway answers, and a successful one clears the SDK's lastError,
  // which is what re-opens the gate.
  private probe = async (): Promise<void> => {
    await this._handle?.refresh();
  };

  // Closes the handle it was handed if the catch-up fails, so a rejected open leaves nothing
  // half-attached and nothing leaked in the SDK's handle cache.
  private async firstCatchUp(handle: SessionHandle): Promise<void> {
    try {
      await handle.refresh();
    } catch (cause) {
      await handle.close();
      const failure = translate(cause, 'open session');
      this.logger.warn(`session open failed: ${failure.message}`);
      throw failure;
    }
  }

  private attach(
    sessionId: string,
    handle: SessionHandle,
    unsubscribe: Unsubscribe,
    unsubscribeStatus: Unsubscribe,
  ): void {
    this._handle = handle;
    this._unsubscribe = unsubscribe;
    this._unsubscribeStatus = unsubscribeStatus;
    this._sessionId = sessionId;
    this._state = handle.state;
  }

  // Unsubscribe before close, and both before anything else is attached: an unsubscribed listener
  // cannot write a dead session's fold into `_state`, whatever the handle does on its way out.
  private async detach(): Promise<void> {
    const handle = this._handle;
    this._unsubscribe?.();
    this._unsubscribeStatus?.();
    this._unsubscribe = undefined;
    this._unsubscribeStatus = undefined;
    this._handle = undefined;
    this._sessionId = undefined;
    this._state = emptySessionState();
    this.reach.reset();
    await handle?.close();
  }
}
